import { derivePaxTypeFromAgeOrSelection } from "./derivePaxTypeFromAgeOrSelection";
import { findPassengerSlotTypeMismatches } from "./passengerSlotConsistency";
import { normalizePassengerInput } from "./normalizePassengerInput";
import {
  LDS_PASSENGERS_VALIDATION_USER_MESSAGE,
  LdsPassengersValidationError,
  validateLdsPassengersConsistency,
} from "../validateLdsPassengersConsistency";
import { sanitizeLdsPassengerValidationLog } from "./sanitizeLdsPassengerValidationLog";

/**
 * @param {unknown} g
 * @returns {'M'|'F'|'X'|null}
 */
export function normalizeGender(g) {
  if (g == null || g === "") return null;
  const s = String(g).trim().toUpperCase();
  if (s === "M" || s === "MALE") return "M";
  if (s === "F" || s === "FEMALE") return "F";
  if (s === "X" || s === "OTHER" || s === "U" || s === "UNSPECIFIED") return "X";
  return null;
}

/**
 * Backward compatibility: infer gender from legacy UI title when `gender` was never collected.
 * @param {unknown} title
 * @returns {'M'|'F'|null}
 */
export function inferGenderFromLegacyTitle(title) {
  if (title == null || title === "") return null;
  const t = String(title).trim().toUpperCase();
  if (t === "MR" || t === "MSTR") return "M";
  if (t === "MS" || t === "MISS") return "F";
  return null;
}

/**
 * LDS paxTitle — derived from paxType + gender (not from free-form UI title).
 * @param {'ADULT'|'CHILD'|'INFANT'} paxType
 * @param {'M'|'F'|'X'|null} genderNorm
 * @returns {string}
 */
export function mapLdsPaxTitle(paxType, genderNorm) {
  if (paxType === "INFANT") return "INFANT";
  if (paxType === "CHILD") {
    if (genderNorm === "F") return "MISS";
    if (genderNorm === "M") return "MSTR";
    return "MSTR";
  }
  if (paxType === "ADULT") {
    if (genderNorm === "F") return "MS";
    if (genderNorm === "M") return "MR";
    return "MR";
  }
  return "MR";
}

/**
 * Effective gender for LDS title rules: explicit gender, else legacy title hint.
 * @param {Record<string, unknown>} pax
 * @returns {'M'|'F'|'X'|null}
 */
export function resolveGenderForLdsTitle(pax) {
  const fromField = normalizeGender(pax?.gender);
  if (fromField && fromField !== "X") return fromField;
  if (fromField === "X") return "X";
  return inferGenderFromLegacyTitle(pax?.title);
}

/**
 * Single passenger: returns fields consumed by backend → LDS XML (title = paxTitle source).
 * @param {Record<string, unknown>} pax
 * @param {{ slot: 'adults'|'kids'|'infants' }} ctx
 * @returns {Record<string, unknown>}
 */
export function mapPassengerToLdsPayload(pax, { slot }) {
  const normalized = normalizePassengerInput(pax);
  const paxType = derivePaxTypeFromAgeOrSelection({ slot });
  const genderNorm = resolveGenderForLdsTitle(normalized);
  const title = mapLdsPaxTitle(
    paxType,
    genderNorm === "X" ? null : genderNorm
  );
  const {
    internalPaxType: _dropInternal,
    pax_type: _dropSnake,
    paxType: _dropPaxType,
    ...rest
  } = normalized;
  return {
    ...rest,
    title,
    paxType,
    internalPaxType: paxType,
  };
}

/**
 * Contact block in buildReservation (first adult).
 * @param {Record<string, unknown>|null|undefined} firstAdult
 */
export function mapContactToLdsPayload(firstAdult) {
  if (!firstAdult || typeof firstAdult !== "object") {
    return {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
    };
  }
  const n = normalizePassengerInput(firstAdult);
  return {
    firstName: n.firstname ?? "",
    lastName: n.lastname ?? "",
    phone: n.cell ?? "",
    email: n.email ?? "",
  };
}

/**
 * Deep clone roomsAndPaxInfo and set each passenger's `title` to the LDS-derived paxTitle source.
 * Other fields (passport, nationality, …) are preserved for DB / future suppliers.
 *
 * @param {unknown} roomsAndPaxInfo
 * @returns {Array}
 */
export function applyLdsPassengerProjectionToRoomsAndPaxInfo(roomsAndPaxInfo) {
  const next = JSON.parse(JSON.stringify(roomsAndPaxInfo || []));
  if (!Array.isArray(next)) return [];
  next.forEach((room) => {
    (room.adults || []).forEach((p, i) => {
      room.adults[i] = mapPassengerToLdsPayload(p, { slot: "adults" });
    });
    (room.kids || []).forEach((p, i) => {
      room.kids[i] = mapPassengerToLdsPayload(p, { slot: "kids" });
    });
    (room.infants || []).forEach((p, i) => {
      room.infants[i] = mapPassengerToLdsPayload(p, { slot: "infants" });
    });
  });
  return next;
}

/**
 * Call at save/API boundaries so UI state stays supplier-neutral; LDS gets derived titles.
 * Slot is authoritative for paxType; internalPaxType / paxType / pax_type are replaced with the slot value.
 *
 * @param {unknown} roomsAndPaxInfo
 * @param {{ context?: string, productType?: string }} [options]
 */
export function preparePassengersInfoForLdsSave(roomsAndPaxInfo, options = {}) {
  const consistency = validateLdsPassengersConsistency({
    roomsAndPaxInfo,
    context: options?.context ?? "preparePassengersInfoForLdsSave",
    productType: options?.productType ?? "unknown",
  });
  if (!consistency.ok) {
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      try {
        // eslint-disable-next-line no-console
        console.error("[LDS_BOOK_CHILD_AS_ADULT_TRACE]", "validation_block", 
          sanitizeLdsPassengerValidationLog(consistency.errors, consistency.traces)
        );
      } catch {
        /* ignore */
      }
    }
    throw new LdsPassengersValidationError(
      LDS_PASSENGERS_VALIDATION_USER_MESSAGE,
      {
        code: "LDS_PASSENGER_OCCUPANCY_MISMATCH",
        issues: consistency.structuredIssues ?? consistency.errors,
        traces: consistency.traces,
      }
    );
  }

  const mismatches = findPassengerSlotTypeMismatches(roomsAndPaxInfo);
  if (
    mismatches.length > 0 &&
    typeof console !== "undefined" &&
    typeof console.warn === "function"
  ) {
    console.warn(
      "[LDS passengers] internalPaxType/paxType/pax_type conflicted with room slot (adults|kids|infants). Overriding to slot for save. Details:",
      mismatches
    );
  }
  return applyLdsPassengerProjectionToRoomsAndPaxInfo(roomsAndPaxInfo);
}

export {
  LdsPassengersValidationError,
  LDS_PASSENGERS_VALIDATION_USER_MESSAGE,
  validateLdsPassengersConsistency,
} from "../validateLdsPassengersConsistency";
